// src/lib/seo-meta.js
// Per-URL meta tags (title, description, canonical, OG, Twitter, robots).
// Pure function. Consumes the same context as buildJsonLd.
// Returns { title, description, canonical, ogImage, ogType, ogUrl, robots }.
import { TOURS_PUBLIC } from './tours-visibility.js';
import { sanitizePriceText, entitySeoPriceMin } from './seo-price-sanitizer.js';
import { BRAND } from '../lib/brand-config.js';
import { variantCanonicalPath } from './fleet-variants.js';

function destinationSlug(d) {
  return d?.slug || (d?.name ? d.name.toLowerCase().replace(/\s+/g, '-') : null);
}

function absoluteUrl(url, siteUrl) {
  if (!url || typeof url !== 'string') return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return siteUrl + (url.startsWith('/') ? url : '/' + url);
}

function clamp(str, max) {
  if (!str) return str;
  if (str.length <= max) return str;
  return str.slice(0, max - 3).trimEnd() + '...';
}

// Optimise an OG/Twitter share image for the social-card-recommended 1200x630
// dimension. Sanity CDN gets ?w=1200&h=630&q=80&fm=jpg&fit=crop (jpg over webp
// because Facebook crawlers don't always handle WebP). Unsplash gets size +
// crop hints. External URLs return unchanged.
function optimizeOgImage(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.includes('cdn.sanity.io') && !url.includes('?')) {
    return url + '?w=1200&h=630&q=80&fm=jpg&fit=crop';
  }
  if (url.includes('images.unsplash.com')) {
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'w=1200&h=630&fit=crop&auto=format&q=80';
  }
  return url;
}

// Find the matching seoContent doc for a given entity. Used as a fallback when
// the entity itself doesn't carry metaTitle/metaDesc overrides. Skipped for
// types whose entity already has native meta fields (aircraft, blogPost).
function findSeoContent(allData, entityType, entity) {
  if (!entity || !entityType) return null;
  if (entityType === 'aircraft' || entityType === 'blogPost') return null;
  const seoContents = allData?.seoContents;
  if (!Array.isArray(seoContents) || !seoContents.length) return null;
  // Map entityType → seoContent.pageType. Two special cases:
  //   destination     → 'dest'   (legacy short-form)
  //   charterService  → 'charter' (matches the generator's pageType output)
  const pageType = entityType === 'destination'
    ? 'dest'
    : entityType === 'charterService' ? 'charter'
    : entityType;
  const slug = entityType === 'destination'
    ? destinationSlug(entity)
    : entityType === 'route' ? entity.slug
    : entityType === 'charterService' ? entity.slug
    : entity.id;
  if (!slug) return null;
  return seoContents.find(s => s?.pageType === pageType && s?.slug === slug) || null;
}

export function buildMetaTags(context) {
  const { path, siteUrl, settings, allData, entity, entityType, seoContent: ctxSeo } = context;
  const s = settings || {};
  const brand = s.siteName || BRAND.name;
  const defaultTitle = s.tagline ? `${s.tagline} | ${brand}` : `Private Helicopter Charter Greece | ${brand}`;
  const defaultDescription = 'Private helicopter charter across Greece. Mykonos, Santorini, Paros, and 30+ destinations. HCAA-licensed operators, instant booking.';
  // Brand share-image fallback chain: curated settings.ogImage first, then the
  // homepage hero (a real CMS asset always present) — so no page type ships a
  // summary_large_image Twitter card without an image.
  const defaultOgImage = absoluteUrl(s.ogImage, siteUrl) || absoluteUrl(allData?.homepage?.heroImage, siteUrl);

  // Editor-override resolution: prefer entity.metaTitle/metaDesc (curated in
  // the entity's own Studio doc) over seoContent (auto-generated). Pulled here
  // once so each branch can consume the resolved values.
  const seoFallback = ctxSeo || findSeoContent(allData, entityType, entity);
  const metaTitleOverride = entity?.metaTitle || seoFallback?.metaTitle || null;
  const metaDescOverride = entity?.metaDesc || entity?.metaDescription || seoFallback?.metaDesc || null;

  let title, description, canonical, ogImage, ogType = 'website';

  if (entity && entityType === 'route') {
    const min = entity.flightMin ? ` \u2014 ~${entity.flightMin} min` : '';
    title = metaTitleOverride || `${entity.from} to ${entity.to} by Helicopter${min} | ${brand}`;
    description = metaDescOverride || `Private helicopter charter from ${entity.from} to ${entity.to}${entity.flightMin ? ' in ' + entity.flightMin + ' minutes' : ''}. Per-aircraft pricing, HCAA-licensed operators, instant booking.`;
    // Most route docs carry no hero/card image — fall back to the destination
    // image of the arrival city (the same asset the visible split-hero right
    // half resolves) before the site-wide default.
    const destTo = (allData?.destinations || []).find(d => d?.name === entity.to);
    ogImage = absoluteUrl(entity.heroImage || entity.cardImage || destTo?.heroImage || destTo?.image, siteUrl);
    const slug = entity.slug || `${entity.from}-${entity.to}`.toLowerCase().replace(/\s+/g, '-');
    canonical = siteUrl + '/route/' + slug;
  } else if (entity && entityType === 'destination') {
    const destMin = parseInt(entity.time) || 0;
    const destLbl = destMin > 0 ? `~${destMin} min` : '';
    const time = destLbl ? ` \u2014 ${destLbl} from Athens` : '';
    title = metaTitleOverride || `${entity.name} Helicopter Charter${time} | ${brand}`;
    if (metaDescOverride) {
      description = metaDescOverride;
    } else {
      const descParts = [`Fly to ${entity.name} by private helicopter.`];
      if (entity.tag) descParts.push(entity.tag + '.');
      if (destLbl) descParts.push(`${destLbl} from Athens.`);
      descParts.push('Instant quotes, same-day booking.');
      description = descParts.join(' ');
    }
    ogImage = absoluteUrl(entity.heroImage || entity.image, siteUrl);
    canonical = siteUrl + '/dest/' + destinationSlug(entity);
  } else if (entity && entityType === 'aircraft') {
    title = metaTitleOverride || `${entity.name} Private Helicopter Charter | ${brand}`;
    const seatInfo = entity.seats ? ` (up to ${entity.seats} passengers)` : '';
    description = metaDescOverride || entity.desc || `Charter the ${entity.name}${seatInfo} for private flights across Greece.`;
    ogImage = absoluteUrl(entity.image, siteUrl);
    // Duplicate airframes of one model (h120_2/h120_3 → h120, h135_red/
    // h135_silver → h135) canonicalise to the model's main page: same name,
    // same copy, three competing thin URLs otherwise. The page still renders
    // and still returns 200 — only the canonical points elsewhere. Derived from
    // the fleet data (fleet-variants.js), never a hardcoded id list.
    canonical = siteUrl + (variantCanonicalPath(entity.id, allData?.fleet) || ('/fleet/' + entity.id));
    ogType = 'product';
  } else if (entity && entityType === 'hotel') {
    const cityPart = entity.city ? ` \u2014 ${entity.city}` : '';
    title = metaTitleOverride || `${entity.name} Helicopter Transfer${cityPart} | ${brand}`;
    if (metaDescOverride) {
      description = metaDescOverride;
    } else {
      const helipadPart = entity.helipadType ? ` ${entity.helipadType === 'direct' ? 'Direct helipad on property' : 'Helicopter transfer available'}.` : '';
      description = entity.desc || `Private helicopter transfer to ${entity.name}${entity.city ? ', ' + entity.city : ''}.${helipadPart}`;
    }
    ogImage = absoluteUrl(entity.image, siteUrl);
    canonical = siteUrl + '/hotel/' + entity.id;
  } else if (entity && entityType === 'blogPost') {
    title = metaTitleOverride || `${entity.title} | ${brand} Journal`;
    description = metaDescOverride || entity.excerpt;
    ogImage = absoluteUrl(entity.image, siteUrl);
    canonical = siteUrl + '/blog/' + entity.slug;
    ogType = 'article';
  } else if (entityType === 'howItWorks') {
    const hw = entity || {};
    title = `How It Works — Helicopter Charter Greece | ${brand}`;
    description = hw.heroSubtitle || `Browse every helicopter in Greece, see real prices, book in minutes. ${brand} handles operator coordination, payment, and flight briefing.`;
    ogImage = absoluteUrl(hw.heroImage, siteUrl) || defaultOgImage;
    canonical = siteUrl + '/how-it-works';
  } else if (entityType === 'about') {
    const aboutTxt = s?.aboutText;
    title = `About ${brand} | Helicopter Charter Brokerage in Athens`;
    description = aboutTxt
      ? aboutTxt.replace(/\s+/g, ' ').trim()
      : `${brand} is an Athens-based private helicopter charter brokerage connecting travellers with HCAA-licensed, EASA-compliant operators across Greece.`;
    ogImage = defaultOgImage;
    canonical = siteUrl + '/about';
  } else if (entityType === 'safety') {
    const sa = entity || {};
    title = `Safety — HCAA & EASA Certified Operators | ${brand}`;
    description = sa.heroSubtitle || `Every charter on ${brand} is operated by HCAA-licensed, EASA-compliant operators with type-rated pilots (500+ hours), full passenger insurance, and documented pre-flight checks.`;
    ogImage = absoluteUrl(sa.heroImage, siteUrl) || defaultOgImage;
    canonical = siteUrl + '/safety';
  } else if (entityType === 'contact') {
    const phoneStr = s?.phone ? ` Call ${s.phone}` : '';
    const emailStr = s?.email ? ` or email ${s.email}` : '';
    title = `Contact ${brand} | Athens Charter Desk`;
    description = `Reach our charter desk in Athens for private helicopter charters across Greece.${phoneStr}${emailStr}.`;
    ogImage = defaultOgImage;
    canonical = siteUrl + '/contact';
  } else if (entity && entityType === 'charterService') {
    const cs = entity;
    // Generated metaTitle / metaDesc win when curated entity-fields are empty.
    // The metaTitleOverride / metaDescOverride were resolved at the top of
    // this function and cover entity.metaTitle → seoFallback.metaTitle.
    title = metaTitleOverride || `${cs.title} — Helicopter Charter Greece | ${brand}`;
    const longestDesc = metaDescOverride
      || (cs.intro ? cs.intro.replace(/\s+/g, ' ').trim() : null)
      || (seoFallback?.intro ? seoFallback.intro.replace(/\s+/g, ' ').trim() : null)
      || cs.subtitle
      || `${cs.title} by private helicopter across Greece. ${brand} brokers HCAA-licensed operators with instant booking.`;
    description = longestDesc.length > 155 ? longestDesc.slice(0, 152) + '...' : longestDesc;
    ogImage = absoluteUrl(cs.hero, siteUrl) || absoluteUrl(seoFallback?.heroImage, siteUrl) || defaultOgImage;
    canonical = siteUrl + '/' + cs.slug;
  } else if (entity && entityType === 'sightseeingTour') {
    const t = entity;
    const departures = (t.departureLocations || []).filter(Boolean);
    const variants = (t.variants || []).filter(v => v && v.duration);
    const lowestPrice = variants.map(v => v.price).filter(p => typeof p === 'number' && p > 0)[0];
    const durationLabel = variants.length
      ? variants.map(v => v.duration + ' min').join(' / ')
      : (t.duration ? t.duration + ' min' : null);
    const fromPart = departures.length ? ` from ${departures.join(' / ')}` : '';
    title = `${t.name} — Helicopter Sightseeing${fromPart} | ${brand}`;
    const variantDesc = (variants[0] && variants[0].description) || t.desc;
    description = variantDesc
      ? variantDesc.replace(/\s+/g, ' ').trim()
      : `${t.name} by private helicopter${fromPart}${durationLabel ? '. ' + durationLabel + ' aerial tour' : ''}${lowestPrice ? '. From EUR ' + lowestPrice + ' per aircraft.' : '.'}`;
    ogImage = absoluteUrl(t.image, siteUrl) || defaultOgImage;
    canonical = siteUrl + '/tour/' + t.id;
  } else if (entityType === 'categoryList' && entity) {
    // Category listing pages — unique title/description/canonical per category
    // (previously these fell through to the homepage default, producing
    // duplicate meta across /fleet, /hotels, /destinations, /routes, /tours).
    const CAT_META = {
      fleet:        { t: `Helicopter Fleet — Charter Aircraft in Greece | ${brand}`, d: 'Browse every helicopter available for private charter across Greece. Full specifications, passenger capacity, range and instant pricing for each aircraft.', p: '/fleet' },
      hotels:       { t: `Hotel Helicopter Transfers in Greece | ${brand}`, d: 'Direct and nearby helicopter transfers to luxury hotels across the Greek islands. Helipad details, flight times and instant charter pricing.', p: '/hotels' },
      destinations: { t: `Helicopter Destinations in Greece | ${brand}`, d: 'Private helicopter charters to Mykonos, Santorini, Paros and 30+ Greek islands and destinations. Flight times, hubs and instant pricing.', p: '/destinations' },
      routes:       { t: `Helicopter Routes in Greece | ${brand}`, d: 'Every helicopter route across Greece with instant pricing — Athens to the islands and island-to-island private charter transfers.', p: '/routes' },
      tours:        { t: `Helicopter Sightseeing Tours in Greece | ${brand}`, d: 'Aerial sightseeing tours by private helicopter across Greece — Santorini caldera, Athens, Meteora and more. Durations, departures and pricing.', p: '/tours' },
    };
    const m = CAT_META[entity.category] || CAT_META.fleet;
    title = m.t;
    description = m.d;
    ogImage = defaultOgImage;
    canonical = siteUrl + m.p;
  } else if (path === '/terms' || path === '/privacy' || path === '/cookies') {
    // Legal pages — SSR previously served the homepage default title/description
    // on all three (duplicate meta); the SPA already had per-page values.
    const LEGAL_META = {
      '/terms':   { t: `Terms and Conditions | ${brand}`, d: `Terms and conditions for helicopter charter bookings via ${brand}.` },
      '/privacy': { t: `Privacy Policy | ${brand}`, d: `How ${brand} handles personal data for bookings and enquiries (GDPR).` },
      '/cookies': { t: `Cookie Policy | ${brand}`, d: `Cookie usage and consent choices on heligreece.com.` },
    };
    title = LEGAL_META[path].t;
    description = LEGAL_META[path].d;
    ogImage = defaultOgImage;
    canonical = siteUrl + path;
  } else if (path === '/blog' || path === '/blog/') {
    // Blog listing — previously fell through to the homepage defaults
    // (duplicate title/description across two indexable URLs).
    title = `Helicopter Travel Journal | ${brand}`;
    description = 'Guides, route insights and travel stories about private helicopter charter across Greece and the islands.';
    ogImage = defaultOgImage;
    canonical = siteUrl + '/blog';
  } else {
    // Homepage or unknown slug → site-wide defaults
    const hp = allData?.homepage;
    title = hp?.metaTitle || defaultTitle;
    description = hp?.metaDescription || defaultDescription;
    ogImage = defaultOgImage;
    canonical = (path && path !== '/' && path !== '')
      ? siteUrl + (path.startsWith('/') ? path : '/' + path)
      : siteUrl;
  }

  // Robots directive. Default index,follow. While tours are hidden
  // (TOURS_PUBLIC === false) the /tours category page and the /tour/<id> detail
  // pages still return 200 but must not be indexed — reversible via the single
  // flag in tours-visibility.js.
  const isToursSurface = entityType === 'sightseeingTour'
    || (entityType === 'categoryList' && entity?.category === 'tours');
  // Legal pages (owner call, 2026-08-18, crawl-audit finding 2): their body is
  // client-fetched (/api/legal/load stays as-is), so the server HTML of every
  // legalPage URL is the same generic shell under its own canonical — a
  // duplicate signal that can rank for nothing. They stay 200, linked,
  // canonicalised and breadcrumbed, but carry noindex. FOLLOW deliberately:
  // their links to the rest of the site keep counting. Data-driven from the
  // Sanity legalPage slugs — a legal page added in the CMS is covered
  // automatically. (/prohibited joined the rule the same day, second owner
  // call, after the audit showed its SSR is the identical generic shell.)
  const pathSegs = String(path || '').split('/').filter(Boolean);
  const legalSlugSet = new Set((allData?.legalPages || []).map(p => p?.slug).filter(Boolean));
  const isNoindexLegalPage = pathSegs.length === 1 && !entityType
    && legalSlugSet.has(pathSegs[0]);
  const robots = (!TOURS_PUBLIC && isToursSurface) ? 'noindex, nofollow'
    : isNoindexLegalPage ? 'noindex, follow'
    : 'index, follow';

  // Last-ditch "EUR ∞" guard — legacy generator strings (entity.metaDesc /
  // seoContent.metaDesc) may carry the Math.min-over-empty artifact. Substitute
  // the entity's real minimum when one exists, drop the broken sentence when
  // not, and fall back to the site default when nothing survives. Never emits
  // "∞" into <meta>, OG or Twitter tags.
  const priceMin = entitySeoPriceMin(entityType, entity, allData?.routes);
  title = sanitizePriceText(title, priceMin) || defaultTitle;
  description = sanitizePriceText(description, priceMin) || defaultDescription;

  return {
    title: clamp(title, 120),
    description: clamp(description, 320),
    canonical,
    ogImage: optimizeOgImage(ogImage || defaultOgImage),
    ogType,
    ogUrl: canonical,
    robots,
  };
}
